import AdvanceExpense from '@/components/applications/advance-expense/AdvanceExpense.vue';
import UserPicker from '@ebg/vue-common/src/components/user/picker/UserPicker.vue';
import { helpers, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import moment from 'moment/moment';
import Decisions from '@/components/common/decision/Decisions.vue';

const guest = {
  type: 2,
  name: null,
  position: null,
  company: null
}

const host = {
  type: 1,
  user: null,
  user_id: null,
  user_company_job_position_id: null,
  job_positions_loading: false,
  positions: null
}

const line = {
  name: null,
  document_number: null,
  amount: null,
  currency_id: null
}

export default {
  name: 'RepresentationExpenses',
  components: { Decisions, AdvanceExpense, UserPicker },
  props: {
    record: {
      type: Object,
      default: null
    },
    expense_line_id: {
      type: Number,
      default: null
    },
    prop_id: {
      type: Number,
      default: null
    },
    document_type: {
      type: Number,
      default: 1
    },
    status: {
      type: String,
      default: null
    },
    waiting_approval_from: {
      type: Number,
      default: null
    },
    expense_id: {
      type: Number,
      default: null
    },
    chequeLines: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      form: {
        id: null,
        purpose: null,
        date: null,
        location: null,
        guests: [{ ...guest }],
        hosts: [{ ...host }],
        lines: [{ ...line }],
        agreed_terms: null,
        comment: null,
        decision_id: null,
        decision_status: null,
        approvers: []
      }
    }
  },
  validations: {
    form: {
      purpose: { required },
      date: { required },
      location: { required },
      agreed_terms: { required, checked: value => value === true },
      lines: {
        $each: helpers.forEach({
          name: { required },
          document_number: { required },
          currency_id: { required },
          amount: { required }
        })
      }
    }
  },
  computed: {
    totalAmount () {
      let total = this.getReprezExpenseLines.reduce((amount, line) => {
        return amount + Number(line.amount || 0)
      }, 0)
      total = total * 100 / 100;
      return total.toFixed(2);
    },
    chequeAmount () {
      const amount = Number(this.chequeLines?.amount || this.chequeLines?.payout_amount || 0);
      return amount.toFixed(2);
    },
    // cheque recognition can miss discount rows, so the act lines may not add up to the cheque total
    isAmountMismatch () {
      return Number(this.chequeAmount) > 0 && Number(this.totalAmount) > 0 &&
        Math.abs(Number(this.totalAmount) - Number(this.chequeAmount)) >= 0.01;
    },
    getReprezExpenseLines () {
      // we dont duplicate of parent line
      return this.form?.lines?.filter((line) => !(line.model_file_uuid));
    },
    id () {
      return this.form.id ?? this.prop_id;
    },
    baseUrl () {
      return 'advance-forms/expense/' + this.expense_id + '/representation/' + this.id;
    },
    canEdit () {
      return !this.isLoading && !['review', 'view'].includes(this.status);
    },
    currencyOptions () {
      return this.BasicHelper.sortArrayByKeyValue(this.$store.getters.getConfigOptions('currencies', this.$i18n.locale));
    },
    apstatus () {
      return {
        value: '0',
        title: 'test1',
        bicon: 'check'
      }
    },
    isReviewable () {
      return this.isInReview && this.waiting_approval_from == this.$session.get('userId');
    },
    isInReview () {
      return this.status === 'review';
    },
    validMinDate () {
      return moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    },
    validMaxDate () {
      return moment().endOf('month').format('YYYY-MM-DD');
    }
  },
  methods: {
    removeGuest (index) {
      this.form.guests.splice(index, 1);
    },
    addGuest () {
      this.form.guests.push({ ...guest });
    },
    removeHost (index) {
      this.form.hosts.splice(index, 1);
    },
    addHost () {
      this.form.hosts.push({ ...host });
    },
    removeLine (index) {
      this.form.lines.splice(index, 1);
    },
    addLine () {
      this.form.lines?.push({ ...line });
    },
    cancel () {
      this.$emit('cancel');
    },
    save () {
      this.v$.$touch();
      if (this.v$.$invalid) {
        // the confirmation is a styled checkbox without an error state, so without naming it
        // the generic message sends the user looking for red fields that are not there
        this.$store.commit('toastAdd', {
          context: 'danger',
          message: this.v$.form.agreed_terms.$invalid
            ? this.$t('applications.representation-expenses.documents-provided-required')
            : this.$t('general.form-invalid')
        });
        return;
      }
      if (!this.isLoading) {
        this.isLoading = true;
        const reqData = this.BasicHelper.cloneObject(this.form);
        reqData.expense_line_id = this.expense_line_id;
        reqData.document_type_id = this.document_type;
        reqData.ax_account_id = this.chequeLines?.ax_account_id ?? null;
        const method = this.id ? this.ApiRequest.REQUEST_PATCH : this.ApiRequest.REQUEST_POST;
        let requestPath = 'advance-forms/expense/representation/';
        if (this.id) {
          requestPath = requestPath + this.id;
        }
        this.ApiRequest.request(requestPath, method, reqData, (response) => {
          if (response.error) {
            this.ApiRequest.displayErrorDialog(response, this);
          } else {
            this.form = response.data;
            this.$emit('save', { data: response.data });
          }
          this.isLoading = false;
        });
      }
    },
    // requests saved before the empty child placeholder stopped being persisted still carry
    // a contentless child row. It has no description and no amounts, so it would render as
    // a blank act line with a NaN total - there is nothing to put on the act, so skip it
    hasChequeLineContent (child) {
      return !!(child.title || parseFloat(child.amount_without_tax) || parseFloat(child.tax_amount));
    },
    chequeLineAmount (child) {
      const amountWithoutTax = parseFloat(child.amount_without_tax) || 0;
      const taxAmount = parseFloat(child.tax_amount) || 0;
      return Math.round((amountWithoutTax + taxAmount) * 100) / 100;
    },
    loadData () {
      if (!this.id) {
        const chequeLineRows = (this.chequeLines.children ?? [])
          .filter(child => !child.is_check_summary && this.hasChequeLineContent(child))
          .map(child => ({
            name: child.title,
            document_number: this.chequeLines.bill_number,
            currency_id: this.$store.getters.getParamFromConfigByFilter('currencies', 'code', child.currency, 'id'),
            amount: this.chequeLineAmount(child)
          }));
        if (chequeLineRows.length > 0) {
          // drop the empty starter row only once there is something to replace it with,
          // otherwise a cheque whose lines are all filtered out leaves the act with no row
          if (!this.form.lines[0].name && !this.form.lines[0].amount) {
            this.form.lines.shift();
          }
          this.form.lines.push(...chequeLineRows);
        }
        return;
      }
      this.isLoading = true;
      const requestPath = this.baseUrl;
      this.ApiRequest.request(requestPath, this.ApiRequest.REQUEST_GET, [], (response) => {
        if (response.data) {
          this.form = response.data;
          if (response.data.decisions && response.data.decisions.length > 0) {
            /*
            this.form.comment = response.data.decisions[0].comment;
            this.form.decision_id = response.data.decisions[0].id;
            this.form.decision_status = response.data.decisions[0].status;
             */
          }
        }
        if (!response.data?.lines || (this.form.lines && this.form.lines.length < 1)) {
          this.addLine();
        }
        if (!response.data?.guests || (this.form.guests && this.form.guests.length < 1)) {
          this.addGuest();
        }
        if (!response.data?.hosts || (this.form.hosts && this.form.hosts.length < 1)) {
          this.addHost();
        }
        this.isLoading = false;
        this.v$.$reset();
      })
    },
    updateJobPositions (index, user) {
      if (!user) {
        return;
      }
      this.form.hosts[index].user_id = user.id;
      this.form.hosts[index].job_positions_loading = true;
      this.form.hosts[index].user = user;
      this.ApiRequest.request('user/jobs/list?with=job&user_id=' + user.id, this.ApiRequest.REQUEST_GET, [], (response) => {
        this.form.hosts[index].job_positions_loading = false;
        if (response.data) {
          this.form.hosts[index].user.job_positions = response.data.map((el) => {
            return {
              value: el.id,
              text: el.job.name.lv
            }
          });
        }
        this.$forceUpdate();
      });
    },
    changeStatus (status) {
      const reqData = {
        status: status,
        comment: this.form.comment,
        decision_id: this.form.decision_id
      };
      this.isLoading = true;
      this.ApiRequest.request(this.baseUrl + '/decision', this.ApiRequest.REQUEST_PATCH, reqData, (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        } else {
          if (response.data === 'status-changed') {
            this.$emit('changeStatus', { line_id: this.expense_line_id, status: status });
          }
        }
        this.isLoading = false;
      });
    }
  },
  setup () {
    return { v$: useVuelidate() }
  },
  mounted () {
    this.loadData();
    // this.isMounted = true;
  }
}
